// localStorage persistence for Letter Flick.
//
// SPEC §7.1 names the full schema. Phase 4 shipped a today-only subset (puzzle
// id + in-progress / completed blobs); Phase 6 extended it with the lifetime
// stats block (`stats: LifetimeStats`, schema v2); Phase 7 extended it with
// `settings: AppSettings` (v3); Phase 8 (v4) tightened the embedded eventLog
// schema (flick events require a `letter` field). v5 swapped the bar-
// provenance encoding from `[sourceTokenIdx, direction]` to
// `[sourceCell, direction]` to match the v0.7 corpus's per-slot valid-set
// semantics. v6 (this version) adds `todayLocalDate: string` so the day-
// rollover detector can compare dates directly instead of puzzle-id equality;
// the prior id-equality predicate silently failed when the corpus's clamp /
// closest-date fallback served the SAME puzzle id on consecutive days,
// stranding the player in a previous day's completed state.
//
// Stored under a single key (`letterflick:state`) as JSON. The top-level
// `schemaVersion` discriminator gates migrations — loadPersistedState reads
// the version field first, then either parses (v6) or migrates upward through
// v1 → v2 → v3 → v4 → v5 → v6. Each migration step writes the upgraded blob
// back so subsequent loads hit the v6 fast path.
//
// v4 → v5 migration policy (PRE-LAUNCH): we cannot reconstruct `sourceCell`
// from the legacy `sourceTokenIdx` without replaying the game (the token's
// cell at flick time depends on every prior cascade). The migration therefore
// DROPS exitedProvenance entirely on v4 records (both finalExitedProvenance
// on CompletedRecord and exitedProvenance on BoardState). The bar renderer
// degrades gracefully — correct slots render green-only (no orange) — which
// is acceptable pre-launch with no shipped users.
//
// v5 → v6 migration policy: seeds `todayLocalDate: ''` (empty-string
// sentinel). On the next mountDaily, the day-rollover predicate sees
// `'' !== '<today>'` → triggers the rollover branch → resetTodayPreservingStats
// wipes today's blob, lifetime stats + settings survive. No special-case
// logic needed; the rollover branch is the standard path.
//
// Migration chain rationale: keeping each step independent (v1 → v2, v2 → v3,
// v3 → v4, v4 → v5, v5 → v6) reuses prior migration code and makes each step
// independently testable. Intermediate vN objects are constructed in-memory
// as plain records (NOT typed PersistedState — the SCHEMA_VERSION literal is
// 6, so only the final v6 step constructs a typed value); only the final v6
// result is persisted.
//
// Serialization: Maps don't JSON-stringify natively, so cellToToken is
// serialized as an array of `[cell, tokenIdx]` tuples (Map.entries() shape).
// tokenToCell is the inverse of cellToToken; rebuilding it on load is cheap
// and removes a denormalization-bug surface.

import { type GameEvent, isGameEventArray } from './events'
import { HINT_FLAGS_KEY } from './hintFlags'
import { PRACTICE_KEY } from './practiceProgress'
import type { Puzzle } from './puzzle'
import {
  type AppSettings,
  DEFAULT_SETTINGS,
  isValidSettings,
} from './settings'
import {
  type LifetimeStats,
  emptyLifetimeStats,
  isValidLifetimeStats,
} from './stats'
import type { EncodedCell, GameState, TokenIdx } from './state'
import { type SerializedSnapshot, isValidSerializedSnapshot } from './undo'

export const PERSISTENCE_KEY = 'letterflick:state' as const
export const ONBOARDED_KEY = 'letterflick:onboarded' as const
export const SCHEMA_VERSION = 6 as const

// One-shot startup warning when we hydrate a legacy persisted state that has
// no eventLog field. Module-scoped so the warn fires at most once per page
// load even if both deserializeBoardStateInto and deserializeCompletedInto
// are called (in practice only one is, but defensive).
let warnedLegacyEventLog = false
function maybeWarnLegacyEventLog(): void {
  if (warnedLegacyEventLog) return
  warnedLegacyEventLog = true
  console.warn(
    'Letter Flick: legacy persisted state has no eventLog; starting log from current move',
  )
}

export interface CompletedRecord {
  readonly won: boolean
  readonly flickCount: number
  readonly undoCount: number
  // EXTENSION beyond SPEC §7.1 schema: includes the final board so reload
  // after completion renders the final position (not an empty board). Phase 5's
  // end-of-game modal layers on top of this restored state.
  readonly finalCellToToken: ReadonlyArray<readonly [EncodedCell, TokenIdx]>
  readonly finalExitedLetters: readonly string[]
  // Phase 5: event log retained across reload so re-opening the endgame modal
  // regenerates the exact same share grid. Optional for backward compatibility
  // with Phase 4 persisted records that pre-date the field.
  readonly eventLog?: readonly GameEvent[]
  // Bar provenance, parallel to finalExitedLetters. Optional for backward
  // compatibility with records written before the bar-provenance feature
  // shipped (and for v4 records whose token-idx-based provenance was dropped
  // in the v4 → v5 migration); if missing on load, the bar renders correct
  // slots BLUE only (no orange) since the canonical-vs-alt distinction needs
  // length-parallel provenance for the DAG walk. Each entry is a
  // `[sourceCell, direction]` tuple — the same flick-time (source cell,
  // direction) pair the solution-DAG edges are keyed by, so the walk compares
  // them directly. Tuples (not objects) to keep the JSON compact.
  readonly finalExitedProvenance?: ReadonlyArray<readonly [number, number]>
}

export interface BoardState {
  readonly cellToToken: ReadonlyArray<readonly [EncodedCell, TokenIdx]>
  readonly exitedLetters: readonly string[]
  readonly undoCount: number
  readonly flickCount: number
  // Phase 5: see CompletedRecord.eventLog notes. Optional for back-compat.
  readonly eventLog?: readonly GameEvent[]
  // Bar provenance, parallel to exitedLetters. See CompletedRecord.
  // v5 encoding: `[sourceCell, direction]` per entry.
  readonly exitedProvenance?: ReadonlyArray<readonly [number, number]>
}

export interface PersistedState {
  readonly schemaVersion: 6
  readonly todayPuzzleId: string
  // 'YYYY-MM-DD' in the player's local timezone, set whenever the today blob
  // is written by a live play (controller's save in dispatchAndRender +
  // orchestrator's post-game stats overwrite). Empty-string sentinel '' is
  // written by `resetTodayPreservingStats`, `saveSettingsOnly` first-save,
  // and the v5 → v6 migration — all paths that have no live "today" date to
  // claim. The day-rollover predicate compares this against
  // formatLocalDate(new Date()); '' compares unequal to any real date string,
  // so a sentinel-bearing blob always re-enters the rollover branch on next
  // mount (idempotent — rollover just calls resetTodayPreservingStats again).
  readonly todayLocalDate: string
  readonly todayInProgress: BoardState | null
  readonly todayCompleted: CompletedRecord | null
  // Phase 6: lifetime stats. Always present in v2+ — migrations seed
  // emptyLifetimeStats() rather than leaving the field undefined, so consumers
  // never have to fall back at the type level.
  readonly stats: LifetimeStats
  // Phase 7: app-level settings (dark-mode preference). Always present in v3+;
  // v2 → v3 migration seeds DEFAULT_SETTINGS.
  readonly settings: AppSettings
  // Undo stack snapshots for the current in-progress puzzle, oldest first.
  // Optional for back-compat: pre-existing v5 blobs in the wild do not have
  // this field; restoring such a blob yields an empty undo stack (the same
  // broken-state that this field fixes for future saves). Cleared to [] when
  // the puzzle is completed (todayInProgress === null).
  readonly todayUndoSnapshots?: readonly SerializedSnapshot[]
}

// ---------- Load / Save / Clear ----------

export function loadPersistedState(): PersistedState | null {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(PERSISTENCE_KEY)
  } catch {
    // localStorage may throw in private-browsing or sandboxed contexts.
    return null
  }
  if (raw === null) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }

  if (parsed === null || typeof parsed !== 'object') return null
  const obj = parsed as Record<string, unknown>
  const version = obj['schemaVersion']

  if (version === 6) {
    return parseV6(obj)
  }
  if (version === 5) {
    // v5 → v6: seed todayLocalDate=''. Everything else passes through.
    const migrated = migrateV5ToV6(obj)
    if (migrated !== null) savePersistedState(migrated)
    return migrated
  }
  if (version === 4) {
    // v4 → v5 → v6 chain.
    const v5Raw = migrateV4ToV5Raw(obj)
    if (v5Raw === null) return null
    const migrated = migrateV5ToV6(v5Raw)
    if (migrated !== null) savePersistedState(migrated)
    return migrated
  }
  if (version === 3) {
    // v3 → v4 → v5 → v6 chain.
    const v4Raw = migrateV3ToV4Raw(obj)
    if (v4Raw === null) return null
    const v5Raw = migrateV4ToV5Raw(v4Raw)
    if (v5Raw === null) return null
    const migrated = migrateV5ToV6(v5Raw)
    if (migrated !== null) savePersistedState(migrated)
    return migrated
  }
  if (version === 2) {
    // v2 → v3 → v4 → v5 → v6 chain.
    const v3Raw = migrateV2ToV3Raw(obj)
    if (v3Raw === null) return null
    const v4Raw = migrateV3ToV4Raw(v3Raw)
    if (v4Raw === null) return null
    const v5Raw = migrateV4ToV5Raw(v4Raw)
    if (v5Raw === null) return null
    const migrated = migrateV5ToV6(v5Raw)
    if (migrated !== null) savePersistedState(migrated)
    return migrated
  }
  if (version === 1) {
    // v1 → v2 → v3 → v4 → v5 → v6 chain. Intermediate vN values are
    // constructed in memory (raw object shape, not typed PersistedState —
    // the SCHEMA_VERSION type invariant is "6", so we never construct a
    // typed v2/v3/v4/v5 PersistedState anywhere). Each migrator consumes the
    // raw object the same way it would consume a freshly-loaded vN blob
    // from disk.
    const v2Raw = migrateV1ToV2Raw(obj)
    if (v2Raw === null) return null
    const v3Raw = migrateV2ToV3Raw(v2Raw)
    if (v3Raw === null) return null
    const v4Raw = migrateV3ToV4Raw(v3Raw)
    if (v4Raw === null) return null
    const v5Raw = migrateV4ToV5Raw(v4Raw)
    if (v5Raw === null) return null
    const migrated = migrateV5ToV6(v5Raw)
    if (migrated !== null) savePersistedState(migrated)
    return migrated
  }
  return null
}

function parseV6(obj: Record<string, unknown>): PersistedState | null {
  if (typeof obj['todayPuzzleId'] !== 'string') return null

  // todayLocalDate must be a string. Older v6 blobs in the wild could not
  // exist (this is the schema bump), but a hand-edited / corrupted save
  // could lose the field; treat that as missing → empty-string sentinel,
  // which the rollover predicate handles as "force reset on next mount".
  const rawDate = obj['todayLocalDate']
  const todayLocalDate: string = typeof rawDate === 'string' ? rawDate : ''

  const ip = obj['todayInProgress']
  const tc = obj['todayCompleted']
  const stats = obj['stats']
  const settings = obj['settings']

  // Undo snapshots are optional (field added after the v5 schema bump).
  // Validate each entry; if anything fails, drop the whole stack rather than
  // partially restoring (a partial stack would let an undo pop a stale entry
  // and corrupt the live state).
  const rawSnapshots = obj['todayUndoSnapshots']
  let undoSnapshots: readonly SerializedSnapshot[] | undefined
  if (rawSnapshots === undefined) {
    undoSnapshots = undefined
  } else if (Array.isArray(rawSnapshots) && rawSnapshots.every(isValidSerializedSnapshot)) {
    undoSnapshots = rawSnapshots
  } else {
    undoSnapshots = undefined
  }

  return {
    schemaVersion: SCHEMA_VERSION,
    todayPuzzleId: obj['todayPuzzleId'],
    todayLocalDate,
    todayInProgress:
      ip === null ? null : isValidBoardState(ip) ? ip : null,
    todayCompleted:
      tc === null ? null : isValidCompletedRecord(tc) ? tc : null,
    // Stats fallthrough: if a v6 blob has a malformed stats field (corrupted
    // localStorage, hand-edited save, etc.) we keep the today-state intact
    // and reset just the stats block rather than wiping the whole save.
    stats: isValidLifetimeStats(stats) ? stats : emptyLifetimeStats(),
    // Same fallthrough policy for settings: invalid → defaults; preserves
    // everything else.
    settings: isValidSettings(settings) ? settings : DEFAULT_SETTINGS,
    ...(undoSnapshots !== undefined ? { todayUndoSnapshots: undoSnapshots } : {}),
  }
}

// v5 → v6 typed migration. Seeds todayLocalDate='' (empty-string sentinel)
// so the next mountDaily falls into the rollover branch and cleanly resets
// the today blob. Stats + settings + the today body all pass through.
// Undo snapshots pass through as well — they belong to the same puzzle that
// the rollover branch will then wipe on next mount, but on the off chance
// the user reloads INSIDE the rollover window and the blob carries an in-
// progress board for a still-valid puzzle, the snapshots remain consistent.
function migrateV5ToV6(obj: Record<string, unknown>): PersistedState | null {
  if (typeof obj['todayPuzzleId'] !== 'string') return null

  const ip = obj['todayInProgress']
  const tc = obj['todayCompleted']
  const stats = obj['stats']
  const settings = obj['settings']

  const rawSnapshots = obj['todayUndoSnapshots']
  let undoSnapshots: readonly SerializedSnapshot[] | undefined
  if (rawSnapshots === undefined) {
    undoSnapshots = undefined
  } else if (Array.isArray(rawSnapshots) && rawSnapshots.every(isValidSerializedSnapshot)) {
    undoSnapshots = rawSnapshots
  } else {
    undoSnapshots = undefined
  }

  return {
    schemaVersion: SCHEMA_VERSION,
    todayPuzzleId: obj['todayPuzzleId'],
    todayLocalDate: '',
    todayInProgress:
      ip === null ? null : isValidBoardState(ip) ? ip : null,
    todayCompleted:
      tc === null ? null : isValidCompletedRecord(tc) ? tc : null,
    stats: isValidLifetimeStats(stats) ? stats : emptyLifetimeStats(),
    settings: isValidSettings(settings) ? settings : DEFAULT_SETTINGS,
    ...(undoSnapshots !== undefined ? { todayUndoSnapshots: undoSnapshots } : {}),
  }
}

// v4 → v5 raw migration: drop bar-provenance arrays. The on-disk shape is
// structurally identical (still ReadonlyArray<readonly [number, number]>),
// but the meaning of the first tuple element flipped from "sourceTokenIdx"
// to "sourceCell" and we cannot reconstruct the new encoding from the old.
// Keeping legacy tuples would mis-color the bar (mostly orange instead of
// mostly green) on the very next render, so we drop the field. Bar renderer
// falls back to "green-only" for restored slots (see render.ts canonical-
// fallback branch). Lifetime stats + settings always carry forward; only
// the in-progress / completed bar provenance is sacrificed. Returns a raw
// v5-shape object (not a typed PersistedState — see chain commentary in
// loadPersistedState). Consumed by migrateV5ToV6.
function migrateV4ToV5Raw(obj: Record<string, unknown>): Record<string, unknown> | null {
  if (typeof obj['todayPuzzleId'] !== 'string') return null

  const ip = obj['todayInProgress']
  const tc = obj['todayCompleted']
  const stats = obj['stats']
  const settings = obj['settings']

  const stripProvenanceBoard = (raw: unknown): unknown => {
    if (raw === null || typeof raw !== 'object') return raw
    const o = raw as Record<string, unknown>
    if (!('exitedProvenance' in o)) return raw
    const { exitedProvenance: _drop, ...rest } = o
    return rest
  }
  const stripProvenanceCompleted = (raw: unknown): unknown => {
    if (raw === null || typeof raw !== 'object') return raw
    const o = raw as Record<string, unknown>
    if (!('finalExitedProvenance' in o)) return raw
    const { finalExitedProvenance: _drop, ...rest } = o
    return rest
  }

  const ipStripped = stripProvenanceBoard(ip)
  const tcStripped = stripProvenanceCompleted(tc)

  return {
    schemaVersion: 5,
    todayPuzzleId: obj['todayPuzzleId'],
    todayInProgress:
      ipStripped === null ? null : isValidBoardState(ipStripped) ? ipStripped : null,
    todayCompleted:
      tcStripped === null ? null : isValidCompletedRecord(tcStripped) ? tcStripped : null,
    stats: isValidLifetimeStats(stats) ? stats : emptyLifetimeStats(),
    settings: isValidSettings(settings) ? settings : DEFAULT_SETTINGS,
  }
}

// v3 → v4 raw migration: shape passthrough on the surface. The only schema
// change at this step is that embedded GameEvent.flick gained a required
// `letter` field; isValidBoardState / isValidCompletedRecord enforce the new
// shape via isGameEventArray, so any pre-v4 letter-less flick events cause
// the record to fall through to null when later parsed. Returns a raw v4-shape
// object (not a typed PersistedState — see chain commentary in
// loadPersistedState). Consumed by migrateV4ToV5.
function migrateV3ToV4Raw(obj: Record<string, unknown>): Record<string, unknown> | null {
  if (typeof obj['todayPuzzleId'] !== 'string') return null
  const ip = obj['todayInProgress']
  const tc = obj['todayCompleted']
  const stats = obj['stats']
  const settings = obj['settings']
  return {
    schemaVersion: 4,
    todayPuzzleId: obj['todayPuzzleId'],
    todayInProgress: ip === null ? null : isValidBoardState(ip) ? ip : null,
    todayCompleted: tc === null ? null : isValidCompletedRecord(tc) ? tc : null,
    stats: isValidLifetimeStats(stats) ? stats : emptyLifetimeStats(),
    settings: isValidSettings(settings) ? settings : DEFAULT_SETTINGS,
  }
}

// v2 → v3 raw migration: seed settings; everything else passes through.
// Returns a raw v3-shape object (not a typed PersistedState — see chain
// commentary in loadPersistedState). Consumed by migrateV3ToV4.
function migrateV2ToV3Raw(obj: Record<string, unknown>): Record<string, unknown> | null {
  if (typeof obj['todayPuzzleId'] !== 'string') return null
  const ip = obj['todayInProgress']
  const tc = obj['todayCompleted']
  const stats = obj['stats']
  return {
    schemaVersion: 3,
    todayPuzzleId: obj['todayPuzzleId'],
    todayInProgress: ip === null ? null : isValidBoardState(ip) ? ip : null,
    todayCompleted: tc === null ? null : isValidCompletedRecord(tc) ? tc : null,
    stats: isValidLifetimeStats(stats) ? stats : emptyLifetimeStats(),
    settings: DEFAULT_SETTINGS,
  }
}

// v1 → v2 raw migration: returns a plain object shaped like a v2 payload,
// NOT a typed PersistedState (only the current SCHEMA_VERSION literal is
// valid in PersistedState, so we can't construct a `schemaVersion: 2`
// PersistedState without weakening the type). The caller (loadPersistedState)
// feeds the result through the migration chain to the current schema.
function migrateV1ToV2Raw(obj: Record<string, unknown>): Record<string, unknown> | null {
  if (typeof obj['todayPuzzleId'] !== 'string') return null
  const ip = obj['todayInProgress']
  const tc = obj['todayCompleted']
  return {
    schemaVersion: 2,
    todayPuzzleId: obj['todayPuzzleId'],
    todayInProgress: ip === null ? null : isValidBoardState(ip) ? ip : null,
    todayCompleted: tc === null ? null : isValidCompletedRecord(tc) ? tc : null,
    stats: emptyLifetimeStats(),
  }
}

export function savePersistedState(s: PersistedState): void {
  try {
    localStorage.setItem(PERSISTENCE_KEY, JSON.stringify(s))
  } catch (err) {
    // QuotaExceededError, private-mode rejection, etc. Persistence is
    // best-effort — losing it shouldn't crash the player's session.
    console.warn('Letter Flick: failed to save persisted state', err)
  }
}

// Day-rollover helper: wipe today's puzzle blob but PRESERVE lifetime stats
// and settings. Replaces the prior `clearPersistedState` whose removeItem()
// would have wiped the player's lifetime stats alongside today's state once
// Phase 6 stored them in the same key. The Phase 7 settings field is
// preserved the same way (we read it via loadPersistedState first).
//
// todayLocalDate is left as the empty-string sentinel here: the immediately-
// following controller.mount path runs without restore, and the controller's
// first save (on first flick) writes the real date via formatLocalDate. If
// the player never flicks and reloads, the predicate re-fires and calls
// this again — idempotent.
export function resetTodayPreservingStats(stats: LifetimeStats): void {
  const current = loadPersistedState()
  const settings: AppSettings = current === null ? DEFAULT_SETTINGS : current.settings
  savePersistedState({
    schemaVersion: SCHEMA_VERSION,
    todayPuzzleId: '',
    todayLocalDate: '',
    todayInProgress: null,
    todayCompleted: null,
    stats,
    settings,
  })
}

// Phase 7 — settings-only persist. Used by the settings overlay's dark-mode
// radio change handler so changing the theme doesn't risk clobbering an
// in-progress board (which the orchestrator might not have re-saved since the
// last flick). Read-modify-write round-trip keeps the rest of the payload
// untouched. Single-tab safe; concurrent-tab races would clobber but are
// acceptable for v1 (rare; non-critical data).
export function saveSettingsOnly(settings: AppSettings): void {
  const current = loadPersistedState()
  if (current === null) {
    // First-time save: seed an empty payload with these settings. The next
    // mountDaily call will overwrite todayPuzzleId/in-progress as needed.
    // todayLocalDate=''  forces the next mount into the rollover branch,
    // which is the right behavior for a brand-new save with no today state.
    savePersistedState({
      schemaVersion: SCHEMA_VERSION,
      todayPuzzleId: '',
      todayLocalDate: '',
      todayInProgress: null,
      todayCompleted: null,
      stats: emptyLifetimeStats(),
      settings,
    })
    return
  }
  // Settings-only save: spread preserves current.todayLocalDate untouched.
  // Changing the theme must not perturb the day-rollover predicate.
  savePersistedState({ ...current, settings })
}

// Phase 7 — full wipe used by the settings overlay's "Reset all stats"
// button. Removes the game-state blob, the practice-progress blob, and the
// onboarding flag so the next page load starts from the same state as a
// brand-new install. The caller is expected to follow this with
// location.reload() so the orchestrator boots from a known-empty state.
export function wipeAllPersistence(): void {
  try {
    localStorage.removeItem(PERSISTENCE_KEY)
  } catch {
    // Swallow — wiping is best-effort.
  }
  try {
    localStorage.removeItem(PRACTICE_KEY)
  } catch {
    // Same — swallow.
  }
  try {
    localStorage.removeItem(ONBOARDED_KEY)
  } catch {
    // Same — swallow.
  }
  try {
    localStorage.removeItem(HINT_FLAGS_KEY)
  } catch {
    // Same — swallow.
  }
  try {
    // Legacy demo flag — the demoFlags module is deleted, but keep wiping the
    // old key so devices that ran the retired demo subsystem get cleaned up.
    localStorage.removeItem('letterflick:demoFlags')
  } catch {
    // Same — swallow.
  }
}

// ---------- (De)serialization ----------

export function serializeBoardState(state: GameState): BoardState {
  // Map.entries() yields the right tuple order; spread realises it lazily.
  return {
    cellToToken: Array.from(state.cellToToken.entries()),
    exitedLetters: state.exitedLetters.slice(),
    undoCount: state.undoCount,
    flickCount: state.flickCount,
    eventLog: state.eventLog.slice(),
    // Tuple form for JSON compactness. v5 encoding: `[sourceCell, direction]`.
    // Matches the solution-DAG edge key (flick-time source cell + direction) so
    // on-disk provenance and in-graph edges share the same primitive types.
    exitedProvenance: state.exitedProvenance.map(
      (p): readonly [number, number] => [p.sourceCell, p.direction],
    ),
  }
}

// Mutates target IN PLACE (mirrors undo.ts:restoreSnapshot). Caller is
// responsible for having just built target via initState(puzzle), so letters,
// wordLength, and puzzleId are already correct. Rebuilds tokenToCell from
// cellToToken so the two maps cannot drift out of sync.
export function deserializeBoardStateInto(
  target: GameState,
  bs: BoardState,
): void {
  target.cellToToken.clear()
  target.tokenToCell.clear()
  for (const [cell, tokenIdx] of bs.cellToToken) {
    target.cellToToken.set(cell, tokenIdx)
    target.tokenToCell.set(tokenIdx, cell)
  }
  target.exitedLetters.length = 0
  for (const letter of bs.exitedLetters) {
    target.exitedLetters.push(letter)
  }
  // Bar provenance hydration. Legacy records (pre-provenance) leave the array
  // empty — the bar then loses the green-vs-orange distinction for restored
  // slots, falling back to green-only (correctness branch in render.ts).
  target.exitedProvenance.length = 0
  if (bs.exitedProvenance !== undefined) {
    for (const [sourceCell, direction] of bs.exitedProvenance) {
      target.exitedProvenance.push({ sourceCell, direction })
    }
  }
  target.undoCount = bs.undoCount
  target.flickCount = bs.flickCount
  target.status = 'playing'
  // Event-log hydration. Legacy persisted state (pre-Phase 5) has no eventLog;
  // warn once and proceed with an empty log. The share grid generated from such
  // a session will only contain events from moves played after the reload.
  target.eventLog.length = 0
  if (bs.eventLog === undefined) {
    maybeWarnLegacyEventLog()
  } else {
    for (const e of bs.eventLog) {
      target.eventLog.push(e)
    }
  }
}

export function serializeCompleted(state: GameState): CompletedRecord {
  return {
    won: state.status === 'won',
    flickCount: state.flickCount,
    undoCount: state.undoCount,
    finalCellToToken: Array.from(state.cellToToken.entries()),
    finalExitedLetters: state.exitedLetters.slice(),
    eventLog: state.eventLog.slice(),
    finalExitedProvenance: state.exitedProvenance.map(
      (p): readonly [number, number] => [p.sourceCell, p.direction],
    ),
  }
}

export function deserializeCompletedInto(
  target: GameState,
  c: CompletedRecord,
): void {
  target.cellToToken.clear()
  target.tokenToCell.clear()
  for (const [cell, tokenIdx] of c.finalCellToToken) {
    target.cellToToken.set(cell, tokenIdx)
    target.tokenToCell.set(tokenIdx, cell)
  }
  target.exitedLetters.length = 0
  for (const letter of c.finalExitedLetters) {
    target.exitedLetters.push(letter)
  }
  target.exitedProvenance.length = 0
  if (c.finalExitedProvenance !== undefined) {
    for (const [sourceCell, direction] of c.finalExitedProvenance) {
      target.exitedProvenance.push({ sourceCell, direction })
    }
  }
  target.undoCount = c.undoCount
  target.flickCount = c.flickCount
  target.status = c.won ? 'won' : 'lost'
  target.eventLog.length = 0
  if (c.eventLog === undefined) {
    maybeWarnLegacyEventLog()
  } else {
    for (const e of c.eventLog) {
      target.eventLog.push(e)
    }
  }
}

// ---------- Lightweight validators (load-path safety) ----------

function isValidBoardState(v: unknown): v is BoardState {
  if (v === null || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  if (!isTupleArrayOfNumbers(o['cellToToken'])) return false
  if (!isStringArray(o['exitedLetters'])) return false
  if (typeof o['undoCount'] !== 'number') return false
  if (typeof o['flickCount'] !== 'number') return false
  // eventLog is optional. If present, it must be a well-formed GameEvent array;
  // a malformed value falls back to "missing" (the deserializer's
  // legacy-warn + empty-log path).
  const log = o['eventLog']
  if (log !== undefined && !isGameEventArray(log)) return false
  // Bar provenance is optional (legacy records). If present, it must be a
  // well-formed tuple-of-numbers array — same shape constraint as
  // cellToToken. A malformed value invalidates the whole record so we don't
  // silently drop half of an intended-restored bar state.
  const prov = o['exitedProvenance']
  if (prov !== undefined && !isTupleArrayOfNumbers(prov)) return false
  return true
}

function isValidCompletedRecord(v: unknown): v is CompletedRecord {
  if (v === null || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  if (typeof o['won'] !== 'boolean') return false
  if (typeof o['flickCount'] !== 'number') return false
  if (typeof o['undoCount'] !== 'number') return false
  if (!isTupleArrayOfNumbers(o['finalCellToToken'])) return false
  if (!isStringArray(o['finalExitedLetters'])) return false
  const log = o['eventLog']
  if (log !== undefined && !isGameEventArray(log)) return false
  const prov = o['finalExitedProvenance']
  if (prov !== undefined && !isTupleArrayOfNumbers(prov)) return false
  return true
}

function isStringArray(v: unknown): v is string[] {
  if (!Array.isArray(v)) return false
  for (const x of v) {
    if (typeof x !== 'string') return false
  }
  return true
}

function isTupleArrayOfNumbers(
  v: unknown,
): v is ReadonlyArray<readonly [number, number]> {
  if (!Array.isArray(v)) return false
  for (const pair of v) {
    if (!Array.isArray(pair) || pair.length !== 2) return false
    if (typeof pair[0] !== 'number' || typeof pair[1] !== 'number') return false
  }
  return true
}

// ---------- Onboarding flag ----------
//
// Stored under a SEPARATE localStorage key from PersistedState so wiping or
// migrating game state never accidentally resets the onboarded flag (and
// vice versa). Value is an ISO-8601 timestamp string — useful for future
// "what's new since you onboarded" content; null when the player has not
// yet completed the onboarding modal.

export function getOnboardedAt(): string | null {
  try {
    return localStorage.getItem(ONBOARDED_KEY)
  } catch {
    // Treat localStorage failures as "first visit." Worst case the user sees
    // the onboarding modal on each session in a private-mode browser.
    return null
  }
}

export function setOnboardedAt(iso: string): void {
  try {
    localStorage.setItem(ONBOARDED_KEY, iso)
  } catch (err) {
    console.warn('Letter Flick: failed to persist onboarding flag', err)
  }
}

export function clearOnboardedAt(): void {
  try {
    localStorage.removeItem(ONBOARDED_KEY)
  } catch {
    // Same swallow logic as savePersistedState.
  }
}

// Re-exported so callers can refer to the schema-version constant without
// importing Puzzle just for the side effect.
export type { Puzzle }
